import React, { type CSSProperties, useEffect, useRef, useState } from 'react'
import DialogActions from '@mui/material/DialogActions'
import GenericButton from '../../GenericButton'
import { ButtonType } from '../../GenericButton/styles'
import { useTheme } from '@mui/material/styles'
import { Box, Tooltip } from '@mui/material'

interface IButtonProps {
  onClick?: () => void | Promise<void>
  text?: string
  disabled?: boolean
  buttonType?: ButtonType
  loading?: boolean
  hideButton?: boolean
  type?: 'button' | 'submit' | 'reset' | undefined
  tooltip?: string
}

interface DefaultModalActionsProps {
  confirmButtonProps?: IButtonProps
  destructiveButtonProps?: IButtonProps
  wrapperStyle?: CSSProperties
  lastUpdateMeta?: React.ReactNode
  prevSettingsProps?: IButtonProps
}

const DefaultModalActions: React.FC<DefaultModalActionsProps> = ({
  confirmButtonProps = {},
  destructiveButtonProps = {},
  wrapperStyle,
  lastUpdateMeta,
  prevSettingsProps
}) => {
  const {
    buttonType: destructiveButtonType = ButtonType.Secondary,
    text: destructiveButtonText = 'Cancel',
    disabled: destructiveButtonDisabled = false,
    loading: destructiveButtonLoading = false,
    onClick: destructiveOnClick,
    hideButton: hideDestructiveButton = false,
    type: typedestructiveButton
  } = destructiveButtonProps

  const {
    buttonType: confirmButtonType = ButtonType.Primary,
    text: confirmButtonText = 'Confirm',
    disabled: confirmButtonDisabled = false,
    loading: confirmButtonLoading = false,
    hideButton: hideConfirmButton = false,
    onClick: onConfirmClick,
    type: confirmConfirmButton,
    tooltip: confirmButtonTooltip

  } = confirmButtonProps

  const theme = useTheme()
  const [showShadow, setShowShadow] = useState(false)
  const localActionsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const getScrollableParent = (node: HTMLElement | null | undefined): HTMLElement | null => {
      let current = node
      while (current) {
        const style = window.getComputedStyle(current)
        if (/(auto|scroll)/.test(style.overflowY)) return current
        current = current.parentElement
      }
      return null
    }

    const el = getScrollableParent(localActionsRef.current)
    if (!el) return

    const handleScroll = (): void => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 2
      setShowShadow(el.scrollHeight > el.clientHeight && !atBottom)
    }

    handleScroll()
    el.addEventListener('scroll', handleScroll)
    return () => {
      el.removeEventListener('scroll', handleScroll)
    }
  }, [])
  const showDestructiveButton = !hideDestructiveButton
  const showConfirmButton = !hideConfirmButton
  return (
    <DialogActions
      ref={localActionsRef}
      sx={{
        marginTop: 6,
        display: 'flex',
        position: 'absolute',
        bottom: 0,
        right: 0,
        boxShadow: showShadow ? theme.shadows[5] : 'none',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        background: 'white',
        width: '100%',
        padding: theme.spacing(3, 4),
        zIndex: 10,
        ...wrapperStyle
      }}
    >
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: (lastUpdateMeta) ? 'space-between' : 'flex-end' }}>
        {prevSettingsProps && (
          <GenericButton
            label={prevSettingsProps.text}
            buttonType={prevSettingsProps.buttonType ?? ButtonType.Ghost}
            onClick={prevSettingsProps.onClick}
            type={prevSettingsProps.type}
            disabled={prevSettingsProps.disabled}
            sx={{ textDecoration: 'underline' }}
            variant='text'
          />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {lastUpdateMeta}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {showDestructiveButton && (
            <GenericButton
              key="destructiveButton"
              buttonType={destructiveButtonType}
              label={destructiveButtonText}
              // GenericButton renders an icon-only button with no visible text whenever loading
              // is true, regardless of `label` — aria-label keeps an accessible name either way.
              aria-label={destructiveButtonText}
              onClick={(e) => {
                e.stopPropagation()
                if (destructiveOnClick) void destructiveOnClick()
              }}
              disabled={destructiveButtonDisabled}
              loading={destructiveButtonLoading}
              fontWeight="600"
              type={typedestructiveButton}
            />
          )}
          {showConfirmButton && (
            <Tooltip title={confirmButtonTooltip ?? ''} arrow disableHoverListener={!confirmButtonTooltip}>
              <span>
                <GenericButton
                  key="confirmButton"
                  buttonType={confirmButtonType}
                  label={confirmButtonText}
                  aria-label={confirmButtonText}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (onConfirmClick) void onConfirmClick()
                  }}
                  disabled={confirmButtonDisabled}
                  loading={confirmButtonLoading}
                  fontWeight="600"
                  type={confirmConfirmButton}
                />
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    </DialogActions>
  )
}

export default DefaultModalActions
